import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  BookOpen, CalendarDays, FileText, PlayCircle, ChevronsLeft, ChevronsRight, ClipboardCheck, FileSignature, Gauge, HardHat, LogOut, Menu, Moon,
  Package, ReceiptText, ScrollText, Settings, Sun, Users, UserCog, Wrench,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { can, ROLE_LABELS } from "@/lib/format";
import { Logo, Monogram } from "@/components/Brand";
import { GlobalSearch } from "@/components/GlobalSearch";
import { Sheet, SheetContent } from "@/components/ui/sheet";

const NAV = [
  { to: "/", label: "Tableau de bord", icon: Gauge, perm: "dashboard", end: true },
  { to: "/agenda", label: "Agenda", icon: CalendarDays, perm: "agenda" },
  { to: "/receptions", label: "Réceptions", icon: ClipboardCheck, perm: "reception" },
  { to: "/clients", label: "Clients & véhicules", icon: Users, perm: "clients" },
  { to: "/devis", label: "Devis", icon: FileSignature, perm: "quotes" },
  { to: "/or", label: "Ordres de réparation", icon: Wrench, perm: "orders" },
  { to: "/atelier", label: "Vue atelier", icon: HardHat, perm: "mechanic" },
  { to: "/factures", label: "Facturation & caisse", icon: ReceiptText, perm: "billing" },
  { to: "/catalogue", label: "Catalogue & stock", icon: Package, perm: "catalog" },
  { to: "/utilisateurs", label: "Utilisateurs", icon: UserCog, perm: "admin" },
  { to: "/journal", label: "Journal d'audit", icon: ScrollText, perm: "admin" },
  { to: "/parametres", label: "Paramètres", icon: Settings, perm: "admin" },
];

const FICHES = ["mecanicien", "caissier", "reception"];

function NavItems({ collapsed, onNavigate }: { collapsed: boolean; onNavigate?: () => void }) {
  const { user } = useAuth();
  return (
    <nav className="flex flex-col gap-1 px-3">
      {NAV.filter((n) => can(user?.role, n.perm)).map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.end}
          onClick={onNavigate}
          title={n.label}
          data-testid={`nav-${n.to.replace("/", "") || "dashboard"}`}
          className={({ isActive }) =>
            `group relative flex h-11 items-center gap-3 rounded-sm px-3 text-sm transition-colors ${
              isActive ? "bg-accent text-foreground" : "text-muted-foreground hover:bg-accent/60 hover:text-foreground"
            }`
          }
        >
          {({ isActive }) => (
            <>
              {isActive && <span className="absolute left-0 top-2 bottom-2 w-[3px] bg-primary" />}
              <n.icon className="h-[18px] w-[18px] shrink-0" />
              {!collapsed && <span className="truncate">{n.label}</span>}
            </>
          )}
        </NavLink>
      ))}
      <a href="/guide-nms-garage.pdf" target="_blank" rel="noreferrer" onClick={onNavigate} title="Guide d'utilisation (PDF)"
        className="flex h-11 items-center gap-3 rounded-sm px-3 text-sm text-muted-foreground transition-colors hover:bg-accent/60 hover:text-foreground" data-testid="nav-guide">
        <BookOpen className="h-[18px] w-[18px] shrink-0" />
        {!collapsed && <span className="truncate">Guide d'utilisation</span>}
      </a>
      <a href="/video-nms-garage.mp4" target="_blank" rel="noreferrer" onClick={onNavigate} title="Vidéo de démonstration"
        className="flex h-11 items-center gap-3 rounded-sm px-3 text-sm text-muted-foreground transition-colors hover:bg-accent/60 hover:text-foreground" data-testid="nav-video">
        <PlayCircle className="h-[18px] w-[18px] shrink-0" />
        {!collapsed && <span className="truncate">Vidéo de démonstration</span>}
      </a>
      {user && FICHES.includes(user.role) && (
        <a href={`/fiche-${user.role}.pdf`} target="_blank" rel="noreferrer" onClick={onNavigate} title="Ma fiche de poste (PDF)"
          className="flex h-11 items-center gap-3 rounded-sm px-3 text-sm text-muted-foreground transition-colors hover:bg-accent/60 hover:text-foreground" data-testid="nav-role-sheet">
          <FileText className="h-[18px] w-[18px] shrink-0" />
          {!collapsed && <span className="truncate">Ma fiche de poste</span>}
        </a>
      )}
    </nav>
  );
}

function useTheme() {
  const [light, setLight] = useState(localStorage.getItem("nms_theme") === "light");
  useEffect(() => {
    document.documentElement.classList.toggle("light", light);
    localStorage.setItem("nms_theme", light ? "light" : "dark");
  }, [light]);
  return [light, setLight] as const;
}

export default function Layout() {
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(localStorage.getItem("nms_sidebar") === "1");
  const [mobile, setMobile] = useState(false);
  const [light, setLight] = useTheme();
  const nav = useNavigate();

  const toggle = () => {
    localStorage.setItem("nms_sidebar", collapsed ? "0" : "1");
    setCollapsed(!collapsed);
  };

  return (
    <div className="flex min-h-screen">
      <aside className={`sticky top-0 hidden h-screen shrink-0 flex-col border-r border-border bg-card transition-[width] duration-200 lg:flex ${collapsed ? "w-[76px]" : "w-64"}`} data-testid="sidebar">
        <div className="flex h-20 items-center justify-center border-b border-border px-4">
          {collapsed ? <Monogram className="h-8" /> : <Logo variant="flat-white" className="h-12" />}
        </div>
        <div className="flex-1 overflow-y-auto py-4">
          <NavItems collapsed={collapsed} />
        </div>
        <button onClick={toggle} className="flex h-12 items-center justify-center gap-2 border-t border-border text-xs text-muted-foreground transition-colors hover:text-foreground" data-testid="sidebar-toggle">
          {collapsed ? <ChevronsRight className="h-4 w-4" /> : <><ChevronsLeft className="h-4 w-4" /> Replier</>}
        </button>
      </aside>

      <Sheet open={mobile} onOpenChange={setMobile}>
        <SheetContent side="left" className="w-72 bg-card p-0">
          <div className="flex h-20 items-center border-b border-border px-5"><Logo variant="flat-white" className="h-10" /></div>
          <div className="py-4"><NavItems collapsed={false} onNavigate={() => setMobile(false)} /></div>
        </SheetContent>
      </Sheet>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-40 flex h-16 items-center gap-3 border-b border-border bg-background/95 px-4 backdrop-blur md:px-8">
          <button className="lg:hidden" onClick={() => setMobile(true)} data-testid="mobile-menu-btn"><Menu className="h-6 w-6" /></button>
          <div className="flex-1">{can(user?.role, "clients") ? <GlobalSearch /> : <Monogram className="h-6 lg:hidden" />}</div>
          <button onClick={() => setLight(!light)} title="Thème clair / sombre" className="flex h-10 w-10 items-center justify-center rounded-sm border border-border text-muted-foreground transition-colors hover:text-foreground" data-testid="theme-toggle">
            {light ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
          </button>
          <div className="hidden text-right sm:block" data-testid="current-user">
            <div className="text-sm font-semibold leading-tight">{user?.name}</div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{ROLE_LABELS[user?.role || ""]}</div>
          </div>
          <button onClick={() => { logout(); nav("/connexion"); }} title="Déconnexion" className="flex h-10 w-10 items-center justify-center rounded-sm border border-border text-muted-foreground transition-colors hover:border-primary hover:text-primary" data-testid="logout-btn">
            <LogOut className="h-4 w-4" />
          </button>
        </header>
        <main className="flex-1 px-4 py-8 md:px-8 lg:px-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
